import { Analytics } from '@cobliteam/analytics';
import Button from '@cobliteam/colib-ui/Button';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import { useGetUnreadNotificationsAmount, useMarkAllNotificationsAsRead } from '@cobliteam/hooks/src/useEntity/useAlerts';
import { useTranslation } from '@cobliteam/i18n';
import { lazy } from '@cobliteam/lazy-with-retries';
import { NotificationsContainer } from '@cobliteam/ui/components/Notifications';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { useQueryClient } from '@tanstack/react-query';
import { usePrevious } from 'ahooks';
import { Suspense, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

// The list is a Blueprint (SDUI) renderer — pulling `@cobliteam/blueprint-react-renderer`
// (~221 KB). AppHeader mounts on every authenticated page, so a static import would land
// the whole renderer engine in the entry chunk. Both the list and the BlueprintProvider
// it needs are co-lazy'd so Rollup packs them into the same chunk that only fetches on
// popover open.
const BlueprintNotificationsList = lazy(() => import('./BlueprintNotificationsList'));
const BlueprintProvider = lazy(() => import('@cobliteam/dashboard/src/providers/BlueprintProvider/BlueprintProvider').then((m) => ({ default: m.BlueprintProvider })));

const NotificationsButton = () => {
  const { t } = useTranslation('app-header');
  const location = useLocation();
  const { runtimeFlags } = useDashboard();
  const prevLocation = usePrevious(location);

  const [open, setOpen] = useState(false);
  const [listKey, setListKey] = useState(0);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && document.querySelector('[role="menu"][data-state="open"]')) return;
    setOpen(nextOpen);
  };

  const queryClient = useQueryClient();
  const { data: unreadAmount, refetch } = useGetUnreadNotificationsAmount();
  const { mutate: markAllAsRead, isSuccess: markSuccess, isError: markError } = useMarkAllNotificationsAsRead();

  useEffect(() => {
    if (location !== prevLocation) setOpen(false);
  }, [location, prevLocation]);

  useEffect(() => {
    if (!open) queryClient.removeQueries({ queryKey: ['PAGES', 'NotificationBell'] });
  }, [open]);

  useEffect(() => {
    if (markSuccess) {
      refetch();
      queryClient.removeQueries({ queryKey: ['PAGES', 'NotificationBell'] });
      setListKey((k) => k + 1);
      toast.success(t('alerts.markAllReadSuccess'));
    }
    if (markError) toast.error(t('alerts.markAllReadError'));
  }, [markSuccess, markError, t]);

  if (runtimeFlags.noValidGroupOrVehicles) return null;

  const amountLabel = unreadAmount && unreadAmount < 10 ? String(unreadAmount) : '9+';

  const trigger = (
    <div
      className='relative'
      onClick={() => {
        if (!open) Analytics.track('NavBar - Notification - Open');
      }}
    >
      <Button variant='outline' size='icon-sm' data-testid='app-header-notifications-button'>
        <Icon name='bell' />
      </Button>
      {unreadAmount ? <div className='absolute -top-1 -right-2 rounded-full bg-danger px-[5px] text-[10px] text-white'>{amountLabel}</div> : null}
    </div>
  );

  return (
    <NotificationsContainer open={open} onOpenChange={handleOpenChange} trigger={trigger} unreadCount={unreadAmount} onMarkAllAsRead={() => markAllAsRead()}>
      <ErrorBoundary
        fallback={
          <Typography variant='text-sm' color='secondary' className='p-4'>
            {t('notifications.error')}
          </Typography>
        }
        onError={(error, errorInfo) => {
          DatadogTrack.error(error, {
            component: 'NotificationsButton',
            section: 'AppHeader',
            errorInfo,
          });
        }}
      >
        <Suspense fallback={null}>
          <BlueprintProvider>
            <BlueprintNotificationsList key={listKey} />
          </BlueprintProvider>
        </Suspense>
      </ErrorBoundary>
    </NotificationsContainer>
  );
};

export default NotificationsButton;
